import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const AddItemDialog = ({ open, onOpenChange, listId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    category: 'other',
    priority: '',
    why_buying: '',
    linked_person: '',
    linked_event_id: '',
    store: '',
    link_url: '',
    size_notes: '',
    budget: '',
    notes: '',
    favorite_id: '',
  });

  useEffect(() => {
    if (open) {
      fetchFavorites();
      fetchEvents();
    }
  }, [open]);

  const fetchFavorites = async () => {
    try {
      const response = await API.get('/shopping/favorites?status=active');
      setFavorites(response.data);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await API.get('/dates?upcoming_days=90');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFavoriteSelect = (favId) => {
    if (favId === 'none') {
      handleChange('favorite_id', '');
      return;
    }
    const fav = favorites.find(f => f.id === favId);
    if (fav) {
      setFormData(prev => ({
        ...prev,
        favorite_id: fav.id,
        name: `${fav.brand || ''} ${fav.name}`.trim(),
        store: fav.store || prev.store,
        link_url: fav.link_url || prev.link_url,
        budget: fav.typical_price || prev.budget,
        size_notes: fav.size_variant || prev.size_notes,
        notes: fav.identification_notes || prev.notes,
        linked_person: fav.who_likes || prev.linked_person,
        why_buying: fav.why_like || prev.why_buying,
      }));
    }
  };

  const handleEventSelect = (eventId) => {
    if (eventId === 'none') {
      handleChange('linked_event_id', '');
      return;
    }
    const event = events.find(e => e.id === eventId);
    if (event) {
      setFormData(prev => ({
        ...prev,
        linked_event_id: event.id,
        why_buying: prev.why_buying || `For ${event.title}`,
        linked_person: event.person_names?.[0] || prev.linked_person,
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      quantity: '',
      category: 'other',
      priority: '',
      why_buying: '',
      linked_person: '',
      linked_event_id: '',
      store: '',
      link_url: '',
      size_notes: '',
      budget: '',
      notes: '',
      favorite_id: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    
    setLoading(true);
    try {
      await API.post('/shopping/items', {
        list_id: listId,
        name: formData.name.trim(),
        quantity: formData.quantity || null,
        category: formData.category,
        priority: formData.priority ? parseInt(formData.priority) : null,
        why_buying: formData.why_buying || null,
        linked_person: formData.linked_person || null,
        linked_event_id: formData.linked_event_id || null,
        store: formData.store || null,
        link_url: formData.link_url || null,
        size_notes: formData.size_notes || null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        notes: formData.notes || null,
        favorite_id: formData.favorite_id || null,
      });
      toast.success('Item added!');
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to add item:', error);
      toast.error('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Shopping Item</DialogTitle>
          <DialogDescription>
            Quick add or fill in details
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Quick select from favorites */}
          {favorites.length > 0 && (
            <div className="space-y-2 p-3 bg-yellow-50 rounded-lg">
              <Label className="text-yellow-800">Quick Add from Favorites</Label>
              <Select value={formData.favorite_id || 'none'} onValueChange={handleFavoriteSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a favorite..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Enter manually --</SelectItem>
                  {favorites.map(fav => (
                    <SelectItem key={fav.id} value={fav.id}>
                      {fav.brand ? `${fav.brand} - ` : ''}{fav.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Item Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="What do you need?"
                data-testid="input-item-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                value={formData.quantity}
                onChange={(e) => handleChange('quantity', e.target.value)}
                placeholder="e.g., 2, 1 lb"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="grocery">Grocery</SelectItem>
                  <SelectItem value="household">Household</SelectItem>
                  <SelectItem value="clothing">Clothing</SelectItem>
                  <SelectItem value="gift">Gift</SelectItem>
                  <SelectItem value="electronics">Electronics</SelectItem>
                  <SelectItem value="health">Health</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Context - Critical */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-green-700">Why am I buying this?</h4>
            <div className="space-y-2">
              <Textarea
                value={formData.why_buying}
                onChange={(e) => handleChange('why_buying', e.target.value)}
                placeholder="e.g., For Mom's birthday, Church potluck, Running low"
                rows={2}
                className="border-green-200 focus:border-green-400"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="linked_person">For Person</Label>
                <Input
                  id="linked_person"
                  value={formData.linked_person}
                  onChange={(e) => handleChange('linked_person', e.target.value)}
                  placeholder="Who is this for?"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Link to Event</Label>
                <Select value={formData.linked_event_id || 'none'} onValueChange={handleEventSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No event --</SelectItem>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title} ({event.date})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {/* Details */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Details (optional)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="store">Store</Label>
                <Input
                  id="store"
                  value={formData.store}
                  onChange={(e) => handleChange('store', e.target.value)}
                  placeholder="Where to buy"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="budget">Budget ($)</Label>
                <Input
                  id="budget"
                  type="number"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority || 'none'} onValueChange={(v) => handleChange('priority', v === 'none' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Normal</SelectItem>
                    <SelectItem value="1">High</SelectItem>
                    <SelectItem value="2">Medium</SelectItem>
                    <SelectItem value="3">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="size_notes">Size / Variant</Label>
                <Input
                  id="size_notes"
                  value={formData.size_notes}
                  onChange={(e) => handleChange('size_notes', e.target.value)}
                  placeholder="e.g., Large, Blue"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="link_url">Link (URL)</Label>
              <Input
                id="link_url"
                value={formData.link_url}
                onChange={(e) => handleChange('link_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any other details..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#D4E5C7] hover:bg-[#C4D5B7] text-gray-900"
              data-testid="submit-item-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Item
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddItemDialog;
